/**
 * Multi-step super-admin login.
 *
 * Stage machine:
 *   credentials → mfa          → (session)   [known IP or device trusted]
 *   credentials → dual_mfa     → (session)   [unknown IP, hard-block OFF — SMS + email]
 *   credentials → ip_blocked   → recovery    [unknown IP, hard-block ON]
 *   credentials → forgot → reset → credentials              [forgot password]
 *   (any)       → recovery     → (session)   [any-2-of-N break-glass recovery]
 *
 * The previous "awaiting_approval" stage (another super-admin had to approve
 * the new device) was removed 2026-05-17. There is only one super admin, so
 * the dual-admin gate was a permanent lockout. MFA still protects login.
 */

import { useState } from "react";
import { useLocation } from "wouter";
import {
  Shield, AlertCircle, Mail, MessageSquare, Loader2, RefreshCw, CheckCircle2,
  KeyRound, LifeBuoy, ArrowLeft, MapPin,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { apiRequest } from "@/lib/queryClient";

type Stage = "credentials" | "mfa" | "dual_mfa" | "ip_blocked" | "forgot" | "reset" | "recovery";

interface MfaState {
  loginToken: string;
  emailHint: string;
  expiresAt: string;
}

interface DualMfaState {
  challengeId: string;
  loginToken: string;
  expiresAt: string;
  hasSms: boolean;
  emailHint: string;
  smsHint: string | null;
  smsVerified: boolean;
  emailVerified: boolean;
}

interface RecoveryAvailability {
  sms: boolean;
  primaryEmail: boolean;
  secondaryEmail: boolean;
  backupCode: boolean;
}

interface RecoveryState {
  challengeId: string;
  recoveryToken: string;
  availability: RecoveryAvailability;
  hints: { phone?: string; primaryEmail?: string; secondaryEmail?: string };
  verified: {
    smsVerified: boolean;
    primaryEmailVerified: boolean;
    secondaryEmailVerified: boolean;
    backupCodeVerified: boolean;
  };
}

const EMPTY_VERIFIED = {
  smsVerified: false,
  primaryEmailVerified: false,
  secondaryEmailVerified: false,
  backupCodeVerified: false,
};

function getReturnTo(): string {
  const params = new URLSearchParams(window.location.search);
  const raw = params.get("returnTo") || "";
  // Only allow internal platform paths to prevent open-redirect abuse.
  if (raw.startsWith("/platform/") || raw === "/platform") return raw;
  return "/platform/dashboard";
}

export default function PlatformLogin() {
  const [, setLocation] = useLocation();
  const [stage, setStage] = useState<Stage>("credentials");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Stage 1
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Stage 2 — single email MFA
  const [mfa, setMfa] = useState<MfaState | null>(null);
  const [code, setCode] = useState("");

  // Stage 3 — dual MFA (unknown IP)
  const [dualMfa, setDualMfa] = useState<DualMfaState | null>(null);
  const [smsCode, setSmsCode] = useState("");
  const [emailCode, setEmailCode] = useState("");
  const [resendLoading, setResendLoading] = useState<"sms" | "email" | null>(null);
  const [resendCooldown, setResendCooldown] = useState<{ sms?: number; email?: number }>({});

  // Forgot / reset password
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");

  // Recovery (any-2-of-N)
  const [recovery, setRecovery] = useState<RecoveryState | null>(null);
  const [recSms, setRecSms] = useState("");
  const [recPrimary, setRecPrimary] = useState("");
  const [recSecondary, setRecSecondary] = useState("");
  const [recBackup, setRecBackup] = useState("");

  const resetToCredentials = () => {
    setStage("credentials");
    setMfa(null);
    setDualMfa(null);
    setRecovery(null);
    setCode("");
    setSmsCode("");
    setEmailCode("");
    setResetToken("");
    setNewPassword("");
    setRecSms(""); setRecPrimary(""); setRecSecondary(""); setRecBackup("");
    setError("");
    setInfo("");
  };

  const submitCredentials = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setIsLoading(true);
    try {
      const res = await apiRequest("POST", "/api/platform/login", { email, password });
      const data = await res.json();
      if (data.stage === "session") {
        window.location.href = getReturnTo();
        return;
      }
      if (data.stage === "mfa") {
        setMfa({ loginToken: data.loginToken, emailHint: data.emailHint, expiresAt: data.expiresAt });
        setStage("mfa");
        return;
      }
      if (data.stage === "dual_mfa") {
        setDualMfa({
          challengeId: data.challengeId,
          loginToken: data.loginToken,
          expiresAt: data.expiresAt,
          hasSms: data.hasSms,
          emailHint: data.emailHint,
          smsHint: data.smsHint,
          smsVerified: false,
          emailVerified: false,
        });
        setStage("dual_mfa");
        return;
      }
      if (data.stage === "ip_blocked") {
        setError("");
        setInfo(data.message || "This IP is blocked. Use account recovery to sign in.");
        setStage("ip_blocked");
        return;
      }
      setError("Unexpected response from server.");
    } catch (err: any) {
      setError(err.message || "Login failed. Please check your credentials.");
    } finally {
      setIsLoading(false);
    }
  };

  const submitMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mfa) return;
    setError("");
    setIsLoading(true);
    try {
      const res = await apiRequest("POST", "/api/platform/login/mfa-verify", {
        loginToken: mfa.loginToken,
        code,
      });
      const data = await res.json();
      if (data.stage === "session") {
        window.location.href = getReturnTo();
        return;
      }
      setError("Unexpected response from server.");
    } catch (err: any) {
      setError(err.message || "Verification failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const submitDualMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dualMfa) return;
    setError("");
    setIsLoading(true);
    try {
      const res = await apiRequest("POST", "/api/platform/login/dual-mfa-verify", {
        challengeId: dualMfa.challengeId,
        loginToken: dualMfa.loginToken,
        ...(emailCode.length === 6 ? { emailCode } : {}),
        ...(smsCode.length === 6 ? { smsCode } : {}),
      });
      const data = await res.json();
      if (data.stage === "session") {
        window.location.href = getReturnTo();
        return;
      }
      if (data.stage === "dual_mfa") {
        setDualMfa((prev) => prev ? { ...prev, smsVerified: data.smsVerified, emailVerified: data.emailVerified } : prev);
        if (data.emailVerified) setEmailCode("");
        if (data.smsVerified) setSmsCode("");
        setError("One code verified. Enter the remaining code.");
        return;
      }
      setError(data.message || "Verification failed.");
    } catch (err: any) {
      setError(err.message || "Verification failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const resendCode = async (method: "sms" | "email") => {
    if (!dualMfa) return;
    setResendLoading(method);
    try {
      const res = await apiRequest("POST", "/api/platform/login/dual-mfa-resend", {
        challengeId: dualMfa.challengeId,
        loginToken: dualMfa.loginToken,
        method,
      });
      const data = await res.json();
      if (data.ok) {
        setResendCooldown((prev) => ({ ...prev, [method]: Date.now() + 60_000 }));
        setError("");
      } else {
        setError(`Wait ${Math.ceil((data.retryAfterMs || 60000) / 1000)}s before resending.`);
      }
    } catch (err: any) {
      setError(err.message || "Resend failed.");
    } finally {
      setResendLoading(null);
    }
  };

  // ── Forgot password ───────────────────────────────────────────────────────
  const submitForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setIsLoading(true);
    try {
      const res = await apiRequest("POST", "/api/platform/forgot-password", { email });
      const data = await res.json();
      setInfo(data.message || "If an account exists, a reset code was emailed.");
      setStage("reset");
    } catch (err: any) {
      setError(err.message || "Could not start password reset.");
    } finally {
      setIsLoading(false);
    }
  };

  const submitReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);
    try {
      const res = await apiRequest("POST", "/api/platform/reset-password", {
        token: resetToken.trim(),
        newPassword,
      });
      const data = await res.json();
      setInfo(data.message || "Password reset. Sign in with your new password.");
      setStage("credentials");
      setPassword("");
      setResetToken("");
      setNewPassword("");
    } catch (err: any) {
      setError(err.message || "Password reset failed.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Break-glass recovery ──────────────────────────────────────────────────
  const startRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setIsLoading(true);
    try {
      const res = await apiRequest("POST", "/api/platform/recovery/init", { email });
      const data = await res.json();
      if (data.stage === "recovery") {
        setRecovery({
          challengeId: data.challengeId,
          recoveryToken: data.recoveryToken,
          availability: data.availability,
          hints: data.hints || {},
          verified: { ...EMPTY_VERIFIED },
        });
        setInfo(data.message || "Recovery codes sent. Prove any TWO factors.");
        setStage("recovery");
      } else {
        // Generic — don't reveal existence.
        setInfo(data.message || "If an account exists, recovery codes were sent.");
      }
    } catch (err: any) {
      setError(err.message || "Could not start recovery.");
    } finally {
      setIsLoading(false);
    }
  };

  const submitRecovery = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!recovery) return;
    setError("");
    setIsLoading(true);
    try {
      const res = await apiRequest("POST", "/api/platform/recovery/verify", {
        challengeId: recovery.challengeId,
        recoveryToken: recovery.recoveryToken,
        ...(recSms ? { smsCode: recSms } : {}),
        ...(recPrimary ? { primaryEmailCode: recPrimary } : {}),
        ...(recSecondary ? { secondaryEmailCode: recSecondary } : {}),
        ...(recBackup ? { backupCode: recBackup } : {}),
      });
      const data = await res.json();
      if (data.stage === "session") {
        // Break-glass success — land on the Security hub and prompt to
        // allowlist the IP we just signed in from.
        if (data.promptAddIp && data.currentIp) {
          window.location.href = `/platform/security?addIp=${encodeURIComponent(data.currentIp)}`;
        } else {
          window.location.href = "/platform/security";
        }
        return;
      }
      if (data.stage === "recovery" && data.state) {
        setRecovery((prev) => prev ? { ...prev, verified: data.state } : prev);
        if (data.state.smsVerified) setRecSms("");
        if (data.state.primaryEmailVerified) setRecPrimary("");
        if (data.state.secondaryEmailVerified) setRecSecondary("");
        if (data.state.backupCodeVerified) setRecBackup("");
        setInfo(`${data.state.verifiedCount}/2 factors verified — prove one more distinct factor.`);
        return;
      }
      setError(data.message || "Recovery failed.");
    } catch (err: any) {
      setError(err.message || "Recovery failed.");
    } finally {
      setIsLoading(false);
    }
  };

  const recVerifiedCount = recovery
    ? [recovery.verified.smsVerified, recovery.verified.primaryEmailVerified, recovery.verified.secondaryEmailVerified, recovery.verified.backupCodeVerified].filter(Boolean).length
    : 0;

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-600 mb-4">
            <Shield className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white">Platform Admin</h1>
          <p className="text-slate-400 mt-1">Clarik Super Admin Access</p>
        </div>

        <Card className="border-red-600 border-2">
          <CardHeader>
            <CardTitle className="text-center">
              {stage === "credentials" && "Sign In"}
              {stage === "mfa" && "Verify Your Email"}
              {stage === "dual_mfa" && "Two-Factor Verification Required"}
              {stage === "ip_blocked" && "IP Not Allowed"}
              {stage === "forgot" && "Reset Your Password"}
              {stage === "reset" && "Enter Reset Code"}
              {stage === "recovery" && "Account Recovery"}
            </CardTitle>
            <CardDescription className="text-center">
              {stage === "credentials" && "Access the platform administration panel"}
              {stage === "mfa" && mfa && `We sent a 6-digit code to ${mfa.emailHint}`}
              {stage === "dual_mfa" && dualMfa && (
                <span>
                  Login from an unrecognized IP. Both{" "}
                  <span className="font-medium">email</span>
                  {dualMfa.hasSms && (<>{" "}and <span className="font-medium">SMS</span></>)}{" "}
                  verification are required.
                </span>
              )}
              {stage === "ip_blocked" && "Password sign-in is disabled from this network."}
              {stage === "forgot" && "We'll email a single-use reset code to your account."}
              {stage === "reset" && "Paste the code from your email and choose a new password."}
              {stage === "recovery" && "Prove any TWO independent factors to regain access."}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-4" data-testid="alert-platform-login-error">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {info && !error && (
              <Alert className="mb-4 border-blue-400 bg-blue-50 dark:bg-blue-950/30" data-testid="alert-platform-login-info">
                <CheckCircle2 className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-800 dark:text-blue-300">{info}</AlertDescription>
              </Alert>
            )}

            {stage === "credentials" && (
              <form onSubmit={submitCredentials} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@clarik.io"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="input-platform-email"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    data-testid="input-platform-password"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={isLoading}
                  data-testid="button-platform-login"
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
                </Button>
                <div className="flex items-center justify-between pt-1">
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 text-xs text-muted-foreground"
                    onClick={() => { setError(""); setInfo(""); setStage("forgot"); }}
                    data-testid="button-platform-forgot"
                  >
                    <KeyRound className="h-3 w-3 mr-1" /> Forgot password?
                  </Button>
                  <Button
                    type="button"
                    variant="link"
                    className="px-0 text-xs text-muted-foreground"
                    onClick={() => { setError(""); setInfo(""); setStage("recovery"); setRecovery(null); }}
                    data-testid="button-platform-recovery"
                  >
                    <LifeBuoy className="h-3 w-3 mr-1" /> Can't sign in?
                  </Button>
                </div>
              </form>
            )}

            {stage === "mfa" && (
              <form onSubmit={submitMfa} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="code" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Verification code
                  </Label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    pattern="\d{6}"
                    placeholder="123456"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\s+/g, ""))}
                    required
                    data-testid="input-platform-mfa-code"
                  />
                  <p className="text-xs text-muted-foreground">
                    Code expires at {new Date(mfa!.expiresAt).toLocaleTimeString()}.
                  </p>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700"
                  disabled={isLoading || code.length !== 6}
                  data-testid="button-platform-verify-mfa"
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify code"}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={resetToCredentials} data-testid="button-platform-mfa-back">
                  Start over
                </Button>
              </form>
            )}

            {stage === "dual_mfa" && dualMfa && (
              <form onSubmit={submitDualMfa} className="space-y-5">
                <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 dark:text-amber-300 text-xs">
                    Your IP is not on the trusted allowlist. You must verify{" "}
                    {dualMfa.hasSms ? "both codes" : "your email code"} to continue.
                    Codes expire at {new Date(dualMfa.expiresAt).toLocaleTimeString()}.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <Label htmlFor="email-code" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" /> Email code
                    {dualMfa.emailVerified && (
                      <Badge className="bg-emerald-500 text-white text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Verified</Badge>
                    )}
                  </Label>
                  {dualMfa.emailVerified ? (
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">✓ Email verified</p>
                  ) : (
                    <>
                      <Input
                        id="email-code" inputMode="numeric" pattern="\d{6}" placeholder="123456"
                        value={emailCode} onChange={(e) => setEmailCode(e.target.value.replace(/\s+/g, ""))}
                        data-testid="input-dual-mfa-email-code"
                      />
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-muted-foreground">Sent to {dualMfa.emailHint}</p>
                        <Button
                          type="button" variant="ghost" size="sm" className="h-6 text-xs"
                          disabled={resendLoading === "email" || (resendCooldown.email ? resendCooldown.email > Date.now() : false)}
                          onClick={() => resendCode("email")} data-testid="button-resend-email-code"
                        >
                          {resendLoading === "email" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                          Resend
                        </Button>
                      </div>
                    </>
                  )}
                </div>

                {dualMfa.hasSms && (
                  <div className="space-y-2">
                    <Label htmlFor="sms-code" className="flex items-center gap-2">
                      <MessageSquare className="h-4 w-4" /> SMS code
                      {dualMfa.smsVerified && (
                        <Badge className="bg-emerald-500 text-white text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Verified</Badge>
                      )}
                    </Label>
                    {dualMfa.smsVerified ? (
                      <p className="text-sm text-emerald-600 dark:text-emerald-400">✓ SMS verified</p>
                    ) : (
                      <>
                        <Input
                          id="sms-code" inputMode="numeric" pattern="\d{6}" placeholder="123456"
                          value={smsCode} onChange={(e) => setSmsCode(e.target.value.replace(/\s+/g, ""))}
                          data-testid="input-dual-mfa-sms-code"
                        />
                        <div className="flex items-center justify-between">
                          <p className="text-xs text-muted-foreground">Sent to {dualMfa.smsHint || "your phone"}</p>
                          <Button
                            type="button" variant="ghost" size="sm" className="h-6 text-xs"
                            disabled={resendLoading === "sms" || (resendCooldown.sms ? resendCooldown.sms > Date.now() : false)}
                            onClick={() => resendCode("sms")} data-testid="button-resend-sms-code"
                          >
                            {resendLoading === "sms" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                            Resend
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                )}

                <Button
                  type="submit" className="w-full bg-red-600 hover:bg-red-700"
                  disabled={isLoading || (!dualMfa.emailVerified && emailCode.length !== 6 && smsCode.length !== 6)}
                  data-testid="button-platform-dual-mfa-verify"
                >
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify codes"}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={resetToCredentials} data-testid="button-platform-dual-mfa-back">
                  Start over
                </Button>
              </form>
            )}

            {stage === "ip_blocked" && (
              <div className="space-y-4">
                <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
                  <MapPin className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 dark:text-amber-300 text-xs">
                    Password sign-in is hard-blocked from un-allowlisted networks. Use account
                    recovery — prove any two factors — to sign in and then add this IP.
                  </AlertDescription>
                </Alert>
                <Button
                  type="button" className="w-full bg-red-600 hover:bg-red-700"
                  onClick={() => { setError(""); setInfo(""); setRecovery(null); setStage("recovery"); }}
                  data-testid="button-ip-blocked-recovery"
                >
                  <LifeBuoy className="h-4 w-4 mr-2" /> Start account recovery
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={resetToCredentials} data-testid="button-ip-blocked-back">
                  Back to sign in
                </Button>
              </div>
            )}

            {stage === "forgot" && (
              <form onSubmit={submitForgot} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <Input
                    id="forgot-email" type="email" placeholder="admin@clarik.io"
                    value={email} onChange={(e) => setEmail(e.target.value)} required
                    data-testid="input-forgot-email"
                  />
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={isLoading} data-testid="button-forgot-submit">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Email me a reset code"}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={resetToCredentials} data-testid="button-forgot-back">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
                </Button>
              </form>
            )}

            {stage === "reset" && (
              <form onSubmit={submitReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-token">Reset code</Label>
                  <Input
                    id="reset-token" placeholder="Paste the code from your email"
                    value={resetToken} onChange={(e) => setResetToken(e.target.value)} required
                    data-testid="input-reset-token"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-password">New password</Label>
                  <Input
                    id="reset-password" type="password" placeholder="At least 12 characters"
                    value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required minLength={12}
                    data-testid="input-reset-password"
                  />
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={isLoading || newPassword.length < 12} data-testid="button-reset-submit">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset password"}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={resetToCredentials} data-testid="button-reset-back">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
                </Button>
              </form>
            )}

            {stage === "recovery" && !recovery && (
              <form onSubmit={startRecovery} className="space-y-4">
                <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
                  <LifeBuoy className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 dark:text-amber-300 text-xs">
                    We'll send codes to every channel on your account. You must prove any TWO of:
                    SMS, primary email, secondary email, or a printed backup code.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label htmlFor="recovery-email">Account email</Label>
                  <Input
                    id="recovery-email" type="email" placeholder="admin@clarik.io"
                    value={email} onChange={(e) => setEmail(e.target.value)} required
                    data-testid="input-recovery-email"
                  />
                </div>
                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700" disabled={isLoading} data-testid="button-recovery-start">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send recovery codes"}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={resetToCredentials} data-testid="button-recovery-back">
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back to sign in
                </Button>
              </form>
            )}

            {stage === "recovery" && recovery && (
              <form onSubmit={submitRecovery} className="space-y-4">
                <Alert className="border-amber-400 bg-amber-50 dark:bg-amber-950/30">
                  <AlertCircle className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-800 dark:text-amber-300 text-xs">
                    {recVerifiedCount}/2 factors verified. Enter any codes you have, then verify.
                  </AlertDescription>
                </Alert>

                {recovery.availability.sms && (
                  <div className="space-y-1.5">
                    <Label htmlFor="rec-sms" className="flex items-center gap-2 text-sm">
                      <MessageSquare className="h-4 w-4" /> SMS code
                      {recovery.verified.smsVerified && <Badge className="bg-emerald-500 text-white text-xs">Verified</Badge>}
                    </Label>
                    {!recovery.verified.smsVerified && (
                      <Input id="rec-sms" inputMode="numeric" placeholder={`Code sent to ${recovery.hints.phone || "your phone"}`}
                        value={recSms} onChange={(e) => setRecSms(e.target.value.replace(/\s+/g, ""))} data-testid="input-recovery-sms" />
                    )}
                  </div>
                )}

                {recovery.availability.primaryEmail && (
                  <div className="space-y-1.5">
                    <Label htmlFor="rec-primary" className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4" /> Primary email code
                      {recovery.verified.primaryEmailVerified && <Badge className="bg-emerald-500 text-white text-xs">Verified</Badge>}
                    </Label>
                    {!recovery.verified.primaryEmailVerified && (
                      <Input id="rec-primary" inputMode="numeric" placeholder={`Code sent to ${recovery.hints.primaryEmail || "your email"}`}
                        value={recPrimary} onChange={(e) => setRecPrimary(e.target.value.replace(/\s+/g, ""))} data-testid="input-recovery-primary" />
                    )}
                  </div>
                )}

                {recovery.availability.secondaryEmail && (
                  <div className="space-y-1.5">
                    <Label htmlFor="rec-secondary" className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4" /> Secondary email code
                      {recovery.verified.secondaryEmailVerified && <Badge className="bg-emerald-500 text-white text-xs">Verified</Badge>}
                    </Label>
                    {!recovery.verified.secondaryEmailVerified && (
                      <Input id="rec-secondary" inputMode="numeric" placeholder={`Code sent to ${recovery.hints.secondaryEmail || "your backup email"}`}
                        value={recSecondary} onChange={(e) => setRecSecondary(e.target.value.replace(/\s+/g, ""))} data-testid="input-recovery-secondary" />
                    )}
                  </div>
                )}

                {recovery.availability.backupCode && (
                  <div className="space-y-1.5">
                    <Label htmlFor="rec-backup" className="flex items-center gap-2 text-sm">
                      <KeyRound className="h-4 w-4" /> Backup code
                      {recovery.verified.backupCodeVerified && <Badge className="bg-emerald-500 text-white text-xs">Verified</Badge>}
                    </Label>
                    {!recovery.verified.backupCodeVerified && (
                      <Input id="rec-backup" placeholder="XXXXX-XXXXX"
                        value={recBackup} onChange={(e) => setRecBackup(e.target.value)} data-testid="input-recovery-backup" />
                    )}
                  </div>
                )}

                <Button type="submit" className="w-full bg-red-600 hover:bg-red-700"
                  disabled={isLoading || (!recSms && !recPrimary && !recSecondary && !recBackup)}
                  data-testid="button-recovery-verify">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify factors"}
                </Button>
                <Button type="button" variant="ghost" className="w-full" onClick={resetToCredentials} data-testid="button-recovery-restart">
                  Start over
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
